/**
 * CSS custom properties that theme the public RSVP dialog (JUB-101, JUB-161).
 *
 * The dialog is mounted by every public page, legacy TSX templates and installed
 * template packages alike, and it mounts on page load whenever RSVP is enabled.
 * A missing field in whatever config reaches it must therefore never throw: an
 * exception here blanks the whole invitation, not just the dialog. Every read is
 * optional, and a colour only goes through the WCAG helpers when both sides of
 * the comparison are hex values those helpers can measure. An unset property
 * leaves the dialog on its default (shadcn) styling for that one rule.
 */

import type { CSSProperties } from "react";
import type { HostSurfaceConfig } from "@/lib/event-templates";
import { ensureAccessibleText, ensureAccessibleErrorColor } from "@/pages/public/templates/shared/color";

/** Any config shape at runtime: a full one, a package bridge, or a broken one. */
export type RsvpDialogThemeInput = {
  [Group in keyof HostSurfaceConfig]?: Partial<HostSurfaceConfig[Group]> | null;
} | null | undefined;

const isHex = (value: unknown): value is string =>
  typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());

/** `preferred` adjusted to WCAG AA against `background` when both are measurable. */
const accessibleOn = (preferred: string | undefined, background: string | undefined): string | undefined => {
  if (!preferred) return undefined;
  return isHex(preferred) && isHex(background) ? ensureAccessibleText(preferred, background) : preferred;
};

export type RsvpDialogCssVars = CSSProperties & Record<string, string | undefined>;

export function rsvpDialogCssVars(config: RsvpDialogThemeInput): RsvpDialogCssVars {
  const palette = config?.palette ?? {};
  const surfaces = config?.surfaces ?? {};
  const typography = config?.typography ?? {};
  const hero = config?.hero ?? {};

  const surface = palette.surface;
  // The submit button continues the template's call to action. A config without
  // one falls back to the template's own accent, never to another theme's colour.
  const submitBg = hero.buttonBg ?? palette.accent;
  const submitText = hero.buttonText ?? palette.background;

  return {
    "--rsvp-surface": surface,
    "--rsvp-text": accessibleOn(palette.text, surface),
    "--rsvp-muted": accessibleOn(palette.mutedText, surface),
    "--rsvp-error": isHex(surface) ? ensureAccessibleErrorColor(surface) : undefined,
    "--rsvp-border": surfaces.cardBorder,
    "--rsvp-shadow": surfaces.shadow,
    "--rsvp-body-font": typography.body,
    "--rsvp-heading-font": typography.heading,
    "--rsvp-submit-bg": submitBg,
    "--rsvp-submit-text": accessibleOn(submitText, submitBg),
    "--rsvp-link": accessibleOn(palette.accent, surface),
    "--rsvp-accent": palette.accent,
  };
}
